import { useEffect, useRef, useState } from 'react';
import { formatPrice } from '../../utils/futuresProduct';
import { useTranslation } from '../../i18n/LocaleContext';
import { calcTimedOrderProgress, isTimedOrderPending } from '../../utils/timedPlayConfig';

function TrendIcon({ direction, label }) {
  const isUp = direction === 'UP';
  return (
    <span
      className={`futures-position-card__trend ${isUp ? 'is-up' : 'is-down'}`}
      aria-label={label}
    >
      <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
        {isUp ? (
          <path
            d="M2 12 L6 7 L9 10 L14 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M2 4 L6 9 L9 6 L14 13"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </span>
  );
}

function resolvePriceTone(order, currentPrice) {
  const current = Number(currentPrice);
  const strike = Number(order?.orderPrice);
  if (Number.isNaN(current) || Number.isNaN(strike)) {
    return '';
  }
  if (order.orderTrend === 'UP') {
    return current >= strike ? 'is-up' : 'is-down';
  }
  return current <= strike ? 'is-up' : 'is-down';
}

function resolveStatusLabel(order, t) {
  if (isTimedOrderPending(order)) {
    return t('trade.unsettled');
  }
  if (order.status === 'END') {
    return order.statusLabel || t('trade.stateSettled');
  }
  if (order.status === 'FAIL') {
    return order.statusLabel || order.status || t('common.dash');
  }
  return order.statusLabel || order.status || t('common.dash');
}

export default function TimedPositionCard({ order, product, currentPrice, onSettled }) {
  const { t } = useTranslation();
  const isPending = isTimedOrderPending(order);
  const [progress, setProgress] = useState(() => calcTimedOrderProgress(order));
  const settledRef = useRef(false);

  useEffect(() => {
    settledRef.current = false;
  }, [order?.orderSerialId]);

  useEffect(() => {
    if (!order || !isPending) {
      return undefined;
    }

    function tick() {
      const next = calcTimedOrderProgress(order);
      setProgress(next);
      if (next.completed && !settledRef.current && typeof onSettled === 'function') {
        settledRef.current = true;
        onSettled(order);
      }
    }

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [isPending, onSettled, order]);

  if (!order) {
    return null;
  }

  const pairLabel =
    product?.name ||
    product?.displayName ||
    (order.marketSymbolCode ? `${order.marketSymbolCode}/USDT` : t('common.dash'));
  const periodLabel = order.durationSeconds ? `${order.durationSeconds}s` : t('common.dash');
  const statusLabel = resolveStatusLabel(order, t);
  const fillPrice = order.orderPrice;
  const priceTone = resolvePriceTone(order, currentPrice);
  const trendLabel = order.orderTrend === 'UP' ? t('trade.buyUp') : t('trade.buyDown');
  const dash = t('common.dash');

  return (
    <li className="futures-position-card">
      <div className="futures-position-card__head">
        <div className="futures-position-card__symbol">
          <strong>{pairLabel}</strong>
          <TrendIcon direction={order.orderTrend} label={trendLabel} />
        </div>
        <div className="futures-position-card__actions">
          <span className={`futures-position-card__status ${isPending ? 'is-pending' : ''}`}>
            {statusLabel}
          </span>
        </div>
      </div>

      <div className="futures-position-card__grid">
        <div className="futures-position-card__cell">
          <span className="futures-position-card__label">{t('trade.settlementTime')}</span>
          <span className="futures-position-card__value">{periodLabel}</span>
        </div>
        <div className="futures-position-card__cell">
          <span className="futures-position-card__label">{t('trade.orderAmount')}</span>
          <span className="futures-position-card__value">{formatPrice(order.betAmount, 6)}</span>
        </div>
        <div className="futures-position-card__cell">
          <span className="futures-position-card__label">{t('trade.fillPrice')}</span>
          <span className="futures-position-card__value">{formatPrice(fillPrice, 4)}</span>
        </div>
        <div className="futures-position-card__cell">
          <span className="futures-position-card__label">{t('trade.currentPrice')}</span>
          <span className={`futures-position-card__value ${priceTone}`}>
            {formatPrice(currentPrice, 4)}
          </span>
        </div>
      </div>

      <div className="futures-position-card__foot">
        <div className="futures-position-card__times">
          <span className="futures-position-card__clock" aria-hidden>
            🕐
          </span>
          <div>
            <div className="futures-position-card__time-main">{order.expireTime || dash}</div>
            <div className="futures-position-card__time-sub">{order.createTime || dash}</div>
          </div>
        </div>
        <div className="futures-position-card__summary">
          {!isPending && (
            <div>
              {t('trade.profitLoss')}：
              <strong>{order.profitLoss != null ? formatPrice(order.profitLoss, 6) : dash}</strong>
            </div>
          )}
          <div>
            {t('trade.fee')}：
            <strong>{formatPrice(order.fee, 6)}</strong>
          </div>
        </div>
      </div>

      {isPending && (
        <div
          className="futures-position-card__progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress.percent)}
          aria-label={t('trade.settlementCountdown')}
        >
          <div
            className="futures-position-card__progress-fill"
            style={{ width: `${progress.percent}%` }}
          />
        </div>
      )}
    </li>
  );
}
